import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import { Radio } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CONSENT_KEY = "uc_cookie_consent";

const STATUS_COLOR = {
  succeeded: "text-emerald-300", success: "text-emerald-300", delivered: "text-emerald-300",
  failed: "text-rose-400", blocked: "text-rose-400", warn: "text-amber-300",
  in_flight: "text-amber-300", pending: "text-amber-300", handoff: "text-teal-300",
};

// GLOBAL LOG — Bloomberg-style bottom ticker streaming REAL API calls and agent
// movements. Every entry is clickable: artifact rows open the artifact directly,
// the rest jump into the Mandate Drawer pre-filtered to the right status.
export default function GlobalLogTicker({ onOpenDrawer }) {
  const [rows, setRows] = useState([]);
  const [consented, setConsented] = useState(() => Boolean(localStorage.getItem(CONSENT_KEY)));

  // Stay hidden until the cookie banner is decided — it overlays the same bottom strip
  // and would swallow ticker clicks silently on first visit.
  useEffect(() => {
    if (consented) return;
    const t = setInterval(() => {
      if (localStorage.getItem(CONSENT_KEY)) setConsented(true);
    }, 1500);
    return () => clearInterval(t);
  }, [consented]);

  useEffect(() => {
    const load = () => api.get("/global-log").then((r) => setRows(r.data.log || [])).catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  if (!consented || !rows.length) return null;

  const renderEntry = (r, i) => {
    const inner = (
      <>
        <span className={r.source === "api" ? "text-sky-300" : "text-amber-400/80"}>
          {r.source === "api" ? (r.direction === "in" ? "◀ API" : "API ▶") : "AGENT"}
        </span>
        <span className="text-white/30">
          {new Date(r.at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
        </span>
        <span className="text-white/65">{r.label}</span>
        <span className={`uppercase tracking-widest ${STATUS_COLOR[r.status] || "text-white/40"}`}>{r.status}</span>
      </>
    );
    const cls = "inline-flex items-center gap-1.5 mx-6 font-mono-uc text-[10px] whitespace-nowrap cursor-pointer hover:opacity-100 opacity-90 transition-opacity";
    if (r.href) {
      const url = r.href.startsWith("/api") ? `${BACKEND}${r.href}` : r.href;
      return (
        <a key={i} href={url} target="_blank" rel="noreferrer" className={cls}
           title="Open artifact" data-testid={`ticker-entry-artifact-${i}`}>
          {inner}
        </a>
      );
    }
    return (
      <button key={i} type="button" className={cls}
              title={`Open Mandate Drawer · ${r.drawer || "active"}`}
              data-testid={`ticker-entry-drawer-${i}`}
              onClick={() => onOpenDrawer?.(r.drawer || "active")}>
        {inner}
      </button>
    );
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 h-8 border-t border-amber-500/25 flex items-center overflow-hidden"
         style={{ background: "rgba(8,8,8,0.96)", backdropFilter: "blur(8px)" }}
         data-testid="global-log-ticker">
      <div className="flex-none h-full px-3 flex items-center gap-1.5 border-r border-white/10 bg-amber-500/5">
        <Radio size={10} className="text-amber-400 uc-pulsing" />
        <span className="font-mono-uc text-[9px] uppercase tracking-[0.25em] text-amber-400/90">Global Log</span>
      </div>
      <div className="flex-1 overflow-hidden relative h-full flex items-center">
        <div className="uc-ticker-track flex items-center" data-testid="global-log-ticker-track">
          {rows.map(renderEntry)}
          {rows.map((r, i) => renderEntry(r, i + rows.length))}
        </div>
      </div>
    </div>
  );
}
